import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { ArrowLeft, ArrowRight, Leaf, Loader2, Mail, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Questions du diagnostic "Clarté intérieure" - Comprendre le changement
const QUESTIONS_FR = [
  {
    id: "ec1",
    text: "Comment décrirais-tu ta situation actuelle ?",
    category: "stade_changement",
    options: [
      { value: 1, text: "Je sens que quelque chose doit changer mais je ne sais pas quoi" },
      { value: 2, text: "Je sais ce que je veux changer mais je n'ose pas commencer" },
      { value: 3, text: "J'ai commencé à changer mais c'est difficile" },
      { value: 4, text: "Je suis en pleine transformation, parfois déstabilisé(e)" },
      { value: 5, text: "Je consolide un changement déjà amorcé" }
    ]
  },
  {
    id: "ec2",
    text: "Comment te sens-tu émotionnellement ces derniers temps ?",
    category: "etat_emotionnel",
    options: [
      { value: 1, text: "Épuisé(e), vidé(e) de mon énergie" },
      { value: 2, text: "Anxieux(se), inquiet(e) pour l'avenir" },
      { value: 3, text: "Confus(e), entre deux eaux" },
      { value: 4, text: "Plutôt optimiste malgré les doutes" },
      { value: 5, text: "Motivé(e) et déterminé(e)" }
    ]
  },
  {
    id: "ec3",
    text: "Quel est ton niveau d'énergie au quotidien ?",
    category: "energie",
    options: [
      { value: 1, text: "Très bas, je me traîne" },
      { value: 2, text: "Fluctuant, des hauts et des bas" },
      { value: 3, text: "Correct mais pas au top" },
      { value: 4, text: "Plutôt bon dans l'ensemble" },
      { value: 5, text: "Plein d'énergie et d'envie" }
    ]
  },
  {
    id: "ec4",
    text: "Qu'est-ce qui te freine le plus dans ton changement ?",
    category: "freins",
    options: [
      { value: 1, text: "La peur de l'inconnu et de l'échec" },
      { value: 2, text: "Le regard des autres et leurs attentes" },
      { value: 3, text: "Le manque de clarté sur ce que je veux vraiment" },
      { value: 4, text: "Les contraintes matérielles ou financières" },
      { value: 5, text: "Rien de particulier, je suis prêt(e)" }
    ]
  },
  {
    id: "ec5",
    text: "As-tu une vision claire de ce vers quoi tu veux aller ?",
    category: "vision",
    options: [
      { value: 1, text: "Pas du tout, c'est le brouillard" },
      { value: 2, text: "J'ai des intuitions mais rien de précis" },
      { value: 3, text: "J'ai quelques idées mais besoin de clarifier" },
      { value: 4, text: "Ma vision commence à se préciser" },
      { value: 5, text: "J'ai une direction claire" }
    ]
  },
  {
    id: "ec6",
    text: "Comment gères-tu l'incertitude ?",
    category: "tolerance_incertitude",
    options: [
      { value: 1, text: "Très mal, j'ai besoin de tout contrôler" },
      { value: 2, text: "Difficilement, ça me stresse beaucoup" },
      { value: 3, text: "Ça dépend des jours" },
      { value: 4, text: "Je m'y habitue progressivement" },
      { value: 5, text: "Je l'accepte comme partie du processus" }
    ]
  },
  {
    id: "ec7",
    text: "Te sens-tu soutenu(e) dans cette période ?",
    category: "soutien",
    options: [
      { value: 1, text: "Non, je me sens très seul(e)" },
      { value: 2, text: "Peu, mon entourage ne comprend pas" },
      { value: 3, text: "Moyennement, j'ai quelques alliés" },
      { value: 4, text: "Oui, j'ai du soutien autour de moi" },
      { value: 5, text: "Très bien entouré(e) et compris(e)" }
    ]
  },
  {
    id: "ec8",
    text: "Quelle est ta relation avec le temps ?",
    category: "rapport_temps",
    options: [
      { value: 1, text: "Je me sens pressé(e), sous pression" },
      { value: 2, text: "J'ai l'impression de perdre du temps" },
      { value: 3, text: "Je suis impatient(e) de voir des résultats" },
      { value: 4, text: "Je commence à accepter que ça prend du temps" },
      { value: 5, text: "Je respecte mon rythme sans me brusquer" }
    ]
  },
  {
    id: "ec9",
    text: "Comment prends-tu soin de toi pendant cette période ?",
    category: "auto_soin",
    options: [
      { value: 1, text: "Je me néglige complètement" },
      { value: 2, text: "J'essaie mais je n'y arrive pas vraiment" },
      { value: 3, text: "De temps en temps, quand j'y pense" },
      { value: 4, text: "Je fais des efforts réguliers" },
      { value: 5, text: "C'est devenu une priorité" }
    ]
  },
  {
    id: "ec10",
    text: "Qu'attends-tu principalement de cet accompagnement ?",
    category: "attentes",
    options: [
      { value: 1, text: "Comprendre ce qui m'arrive" },
      { value: 2, text: "Retrouver de la clarté et du sens" },
      { value: 3, text: "Passer à l'action concrètement" },
      { value: 4, text: "Gérer mes émotions et mon stress" },
      { value: 5, text: "Ancrer durablement mes nouvelles habitudes" }
    ]
  }
];

const QUESTIONS_EN = [
  {
    id: "ec1",
    text: "How would you describe your current situation?",
    category: "stade_changement",
    options: [
      { value: 1, text: "I feel something needs to change but I don't know what" },
      { value: 2, text: "I know what I want to change but I don't dare start" },
      { value: 3, text: "I've started changing but it's difficult" },
      { value: 4, text: "I'm in the middle of transformation, sometimes destabilized" },
      { value: 5, text: "I'm consolidating a change already underway" }
    ]
  },
  {
    id: "ec2",
    text: "How have you been feeling emotionally lately?",
    category: "etat_emotionnel",
    options: [
      { value: 1, text: "Exhausted, drained of energy" },
      { value: 2, text: "Anxious, worried about the future" },
      { value: 3, text: "Confused, in between" },
      { value: 4, text: "Fairly optimistic despite doubts" },
      { value: 5, text: "Motivated and determined" }
    ]
  },
  {
    id: "ec3",
    text: "What is your daily energy level?",
    category: "energie",
    options: [
      { value: 1, text: "Very low, I'm dragging myself" },
      { value: 2, text: "Fluctuating, ups and downs" },
      { value: 3, text: "OK but not at my best" },
      { value: 4, text: "Pretty good overall" },
      { value: 5, text: "Full of energy and desire" }
    ]
  },
  {
    id: "ec4",
    text: "What holds you back most in your change?",
    category: "freins",
    options: [
      { value: 1, text: "Fear of the unknown and failure" },
      { value: 2, text: "Others' opinions and expectations" },
      { value: 3, text: "Lack of clarity about what I really want" },
      { value: 4, text: "Material or financial constraints" },
      { value: 5, text: "Nothing particular, I'm ready" }
    ]
  },
  {
    id: "ec5",
    text: "Do you have a clear vision of where you want to go?",
    category: "vision",
    options: [
      { value: 1, text: "Not at all, it's foggy" },
      { value: 2, text: "I have intuitions but nothing precise" },
      { value: 3, text: "I have some ideas but need to clarify" },
      { value: 4, text: "My vision is starting to take shape" },
      { value: 5, text: "I have a clear direction" }
    ]
  },
  {
    id: "ec6",
    text: "How do you handle uncertainty?",
    category: "tolerance_incertitude",
    options: [
      { value: 1, text: "Very poorly, I need to control everything" },
      { value: 2, text: "With difficulty, it stresses me a lot" },
      { value: 3, text: "It depends on the day" },
      { value: 4, text: "I'm gradually getting used to it" },
      { value: 5, text: "I accept it as part of the process" }
    ]
  },
  {
    id: "ec7",
    text: "Do you feel supported during this period?",
    category: "soutien",
    options: [
      { value: 1, text: "No, I feel very alone" },
      { value: 2, text: "Little, my circle doesn't understand" },
      { value: 3, text: "Moderately, I have some allies" },
      { value: 4, text: "Yes, I have support around me" },
      { value: 5, text: "Very well surrounded and understood" }
    ]
  },
  {
    id: "ec8",
    text: "What is your relationship with time?",
    category: "rapport_temps",
    options: [
      { value: 1, text: "I feel rushed, under pressure" },
      { value: 2, text: "I feel like I'm wasting time" },
      { value: 3, text: "I'm impatient to see results" },
      { value: 4, text: "I'm starting to accept it takes time" },
      { value: 5, text: "I respect my pace without rushing" }
    ]
  },
  {
    id: "ec9",
    text: "How do you take care of yourself during this period?",
    category: "auto_soin",
    options: [
      { value: 1, text: "I completely neglect myself" },
      { value: 2, text: "I try but can't really manage" },
      { value: 3, text: "From time to time, when I think of it" },
      { value: 4, text: "I make regular efforts" },
      { value: 5, text: "It has become a priority" }
    ]
  },
  {
    id: "ec10",
    text: "What do you mainly expect from this support?",
    category: "attentes",
    options: [
      { value: 1, text: "Understand what's happening to me" },
      { value: 2, text: "Find clarity and meaning" },
      { value: 3, text: "Take concrete action" },
      { value: 4, text: "Manage my emotions and stress" },
      { value: 5, text: "Sustainably anchor my new habits" }
    ]
  }
];

const CATEGORY_LABELS = {
  fr: {
    stade_changement: "Stade du changement",
    etat_emotionnel: "État émotionnel",
    energie: "Niveau d'énergie",
    freins: "Freins identifiés",
    vision: "Clarté de vision",
    tolerance_incertitude: "Tolérance à l'incertitude",
    soutien: "Niveau de soutien",
    rapport_temps: "Rapport au temps",
    auto_soin: "Auto-soin",
    attentes: "Attentes"
  },
  en: {
    stade_changement: "Change stage",
    etat_emotionnel: "Emotional state",
    energie: "Energy level",
    freins: "Identified barriers",
    vision: "Vision clarity",
    tolerance_incertitude: "Uncertainty tolerance",
    soutien: "Support level",
    rapport_temps: "Relationship with time",
    auto_soin: "Self-care",
    attentes: "Expectations"
  }
};

export default function EmergenceDiagnostic() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sessionId] = useState(() => uuidv4());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const isFrench = i18n.language === 'fr';
  const questions = isFrench ? QUESTIONS_FR : QUESTIONS_EN;

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSelectOption = (questionId, category, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { question_id: questionId, category, value }
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmitClick = () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error(isFrench ? "Réponds à toutes les questions" : "Answer all questions");
      return;
    }
    setShowEmailModal(true);
  };

  const submitDiagnostic = async (email) => {
    if (!email || !isValidEmail(email)) {
      setEmailError(isFrench ? "Entre un email valide" : "Enter a valid email");
      return;
    }
    
    setSubmitting(true);
    setEmailError("");
    
    try {
      const response = await axios.post(`${API}/emergence/diagnostic/submit`, {
        session_id: sessionId,
        answers: Object.values(answers),
        language: i18n.language,
        user_email: email
      });
      
      setShowEmailModal(false);
      
      if (response.data.email_sent) {
        toast.success(isFrench 
          ? "Tes résultats ont été envoyés par email !" 
          : "Your results have been sent by email!");
      }
      
      toast.success(isFrench ? "Diagnostic terminé !" : "Diagnostic completed!");
      navigate(`/emergence/results/${sessionId}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (isFrench ? "Une erreur est survenue" : "An error occurred");
      setEmailError(errorMsg);
      toast.error(errorMsg);
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  const isAnswered = currentQuestion && answers[currentQuestion.id];

  return (
    <div className="emergence-bg min-h-screen">
      {/* Header */}
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link to="/emergence" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80 transition-opacity" data-testid="back-emergence-link">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{isFrench ? "Retour" : "Back"}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#3B7A8C]" />
              <span className="font-heading text-lg text-[#2D5A66]">{isFrench ? "Clarté intérieure" : "Inner Clarity"}</span>
            </div>
            <span className="text-sm text-[#7A8B99]">
              {currentIndex + 1}/{questions.length}
            </span>
          </div>
          <div className="emergence-progress">
            <div className="emergence-progress-fill" style={{ width: `${progress}%` }} data-testid="progress-bar" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {currentQuestion && (
            <div className="emergence-animate-in" key={currentQuestion.id}>
              {/* Category badge */}
              <div className="mb-6">
                <span className="emergence-badge">
                  {CATEGORY_LABELS[i18n.language]?.[currentQuestion.category] || currentQuestion.category}
                </span>
              </div>

              {/* Question */}
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2D5A66] mb-8 leading-relaxed" data-testid="question-text">
                {currentQuestion.text}
              </h2>

              {/* Options */}
              <div className="space-y-4 mb-12">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(currentQuestion.id, currentQuestion.category, option.value)}
                    className={`emergence-option w-full text-left ${answers[currentQuestion.id]?.value === option.value ? 'selected' : ''}`}
                    data-testid={`option-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        answers[currentQuestion.id]?.value === option.value 
                          ? 'border-[#3B7A8C] bg-[#3B7A8C]' 
                          : 'border-[#7A8B99]/40'
                      }`}>
                        {answers[currentQuestion.id]?.value === option.value && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="text-[#3D4F5F] leading-relaxed">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className="rounded-full px-6 border-[#3B7A8C]/20 text-[#7A8B99]"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {isFrench ? "Précédent" : "Previous"}
                </Button>

                {isLastQuestion ? (
                  <Button
                    onClick={handleSubmitClick}
                    disabled={!isAnswered || submitting}
                    className="emergence-btn-accent"
                    data-testid="submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isFrench ? "Analyse..." : "Analyzing..."}
                      </>
                    ) : (
                      <>
                        {isFrench ? "Voir mon profil" : "See my profile"}
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!isAnswered}
                    className="emergence-btn-primary"
                    data-testid="next-btn"
                  >
                    {isFrench ? "Suivant" : "Next"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={(open) => {
        if (!submitting) {
          setShowEmailModal(open);
          setEmailError("");
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center text-[#2D5A66]">
              {isFrench ? "🌱 Dernière étape" : "🌱 Last step"}
            </DialogTitle>
            <DialogDescription className="text-center text-[#7A8B99]">
              {isFrench 
                ? "Ton email pour recevoir ton profil personnalisé"
                : "Your email to receive your personalized profile"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {emailError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{emailError}</span>
              </div>
            )}
            
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A8B99]" />
              <Input
                type="email"
                placeholder={isFrench ? "Ton email" : "Your email"}
                value={userEmail}
                onChange={(e) => {
                  setUserEmail(e.target.value);
                  setEmailError("");
                }}
                className={`pl-10 h-12 rounded-xl border-2 ${emailError ? 'border-red-300' : 'border-[#3B7A8C]/20'} focus:border-[#3B7A8C]`}
                data-testid="email-input-modal"
                disabled={submitting}
              />
            </div>
            <Button
              onClick={() => submitDiagnostic(userEmail)}
              disabled={!userEmail || submitting}
              className="w-full h-12 emergence-btn-accent"
              data-testid="submit-with-email"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {isFrench ? "Analyse en cours..." : "Analyzing..."}
                </>
              ) : (
                <>
                  {isFrench ? "Découvrir mon profil" : "Discover my profile"}
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-center text-[#7A8B99]">
              {isFrench 
                ? "⚠️ Un email = un diagnostic gratuit"
                : "⚠️ One email = one free diagnostic"}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
